<template>
    <main-dialog
        ref="dialog"
        :translated-header="$tkey('header')"
        :btn-confirm-text="btnConfirmText"
        btn-cancel-text="actions.cancel"
        :has-activator="false"
        width="680"
        @confirm="$emit('confirm')"
        @close="close"
    >
        <template v-slot:default>
            <div v-if="renderGrid" class="confirm-dialog">
                <p class="confirm-dialog__description">
                    {{ toSentenceCase($tkey('actionableText')) }}
                </p>
                <promo-ag-grid
                    ref="agGrid"
                    :row-data="actionableEntities"
                    :column-defs="columnDefs"
                    :grid-options="gridOptions"
                    :default-col-def="defaultColDef"
                    grid-class="task-confirmation-grid"
                    grid-style="width: 100%; height: 20rem;"
                />
                <v-divider />
                <div v-if="excludedEntities.length">
                    <p class="confirm-dialog__description">
                        {{ toSentenceCase($tkey('actionableText')) }}
                    </p>
                    <promo-ag-grid
                        ref="agGridExcluded"
                        :row-data="excludedEntities"
                        :column-defs="columnDefs"
                        :grid-options="gridOptionsExcludedGrid"
                        :default-col-def="defaultColDefExcludedGrid"
                        grid-class="task-confirmation-grid"
                        grid-style="width: 100%; height: 20rem;"
                    />
                </div>
            </div>
        </template>
    </main-dialog>
</template>

<script>
import { markRaw } from 'vue';
import { difference } from 'lodash';
import { mapState } from 'vuex';
import createFeatureAwareFactory from '@/js/feature-toggles/feature-factory';
import { mapWorkflowStateIcons } from '@/js/utils/workflow-utils';
import iconRenderer from '@/js/components/workflow/icon-renderer.vue';
import workflowEntityOperations from './workflow-entity-operations';

export default {
    localizationKey: 'workflow.taskButton.confirmDialog',
    props: {
        btnConfirmText: {
            type: String,
            required: true,
        },
        entityIds: {
            type: Array,
            required: true,
        },
        actionableEntityIds: {
            type: Array,
            required: true,
        },
        entityType: {
            required: true,
            type: String,
        },
    },
    emits: ['confirm', 'close'],
    data() {
        return {
            defaultColDef: {
                editable: false,
                suppressMovable: true, // Stop users from being able to rearrange columns.
                lockPinned: true, // Stop users from being able to pin columns.
                filter: false,
                menuTabs: [],
                resizable: false,
            },
            gridOptions: markRaw({
                suppressContextMenu: true, // only show filter
                headerHeight: 47,
                rowHeight: 40,
                components: {
                    iconRenderer,
                },
            }),
            columnDefs: [],
            // when we use the same otions in two grids ag-grid throws an error
            // Maximum recursive updates exceeded in component <PromoAgGrid>
            // so we need to create a separate one for the excluded entities grid
            defaultColDefExcludedGrid: {
                editable: false,
                suppressMovable: true, // Stop users from being able to rearrange columns.
                lockPinned: true, // Stop users from being able to pin columns.
                filter: false,
                menuTabs: [],
                resizable: false,
            },
            gridOptionsExcludedGrid: markRaw({
                suppressContextMenu: true, // only show filter
                headerHeight: 47,
                rowHeight: 40,
                components: {
                    iconRenderer,
                },
            }),
            renderGrid: false,
            excludedEntities: [],
            actionableEntities: [],
            workflowStateIconsMap: null,
        };
    },

    computed: {
        ...mapState('clientConfig', ['toggleLogic']),
    },

    created() {
        const excludedIds = difference(this.entityIds, this.actionableEntityIds);
        this.excludedEntities = excludedIds.map(id =>
            workflowEntityOperations[this.entityType].getEntityById({ entityId: id })
        );

        this.actionableEntities = this.actionableEntityIds.map(id =>
            workflowEntityOperations[this.entityType].getEntityById({ entityId: id })
        );

        this.workflowStateIconsMap = createFeatureAwareFactory(
            this.toggleLogic
        ).getSubCampaignAllocationWorkflowStateIconsMap();

        const confirmationFields = workflowEntityOperations[
            this.entityType
        ].getConfirmationFields();

        this.columnDefs = [
            {
                headerName: this.toSentenceCase(this.$tkey('gridHeadings.clientSate')),
                cellRenderer: 'iconRenderer',
                valueGetter: params => {
                    const workflowStateIcon = mapWorkflowStateIcons({
                        workflowState: params.data.workflowState,
                        ...this.workflowStateIconsMap,
                    });

                    return {
                        icon: workflowStateIcon.icon,
                        color: 'black',
                        text: this.$t(
                            this.$t(`workflow.taskButton.states.${workflowStateIcon.text}`)
                        ),
                    };
                },
            },
            ...confirmationFields.map(field => {
                return {
                    field: field.field,
                    headerName: this.toSentenceCase(this.$tkey(`gridHeadings.${field.headerName}`)),
                };
            }),
        ];
    },

    methods: {
        open() {
            this.renderGrid = true;
            this.$refs.dialog.open();
        },

        close({ emitOnly = true } = {}) {
            this.renderGrid = false;
            if (emitOnly) {
                this.$emit('close');
            } else {
                this.$refs.dialog.close();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.confirm-dialog {
    font-size: 1.2rem;

    &__description {
        padding: 1rem 0;
    }
}

:deep(.task-confirmation-grid) {
    .ag-cell-wrapper {
        min-width: 0;
        overflow: hidden;
    }

    .ag-cell-value {
        display: block;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    .ag-root-wrapper {
        min-height: 0rem;
    }
}
</style>
